import InterviewSection from '@/components/job/InterviewSection';
import { JobApplicationStatusBadge } from '@/components/job/JobApplicationStatusBadge';
import { JobAnalysis } from '@/components/JobAnalysis';
import { DashboardLayout } from '@/components/layouts/DashboardLayout';
import PitchList from '@/components/pitch/PitchList';
import SalaryNegotiationList from '@/components/negotiation/SalaryNegotiationList';
import PremiumLockModal from '@/components/subscription/PremiumLockModal';
import { JobTodoCard } from '@/components/todos/JobTodoCard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { InterviewResponse, JobAnalysisApiResponse, JobCompleteApiResponse, useApiClient } from '@/lib/api';
import { getUserErrorMessage } from '@/lib/error-messages';
import { format } from 'date-fns';
import { Angry, Briefcase, CalendarDaysIcon, Calendar as CalendarIcon, ChevronLeft, DollarSign, Edit3, FileText, Frown, Globe, Link, Linkedin, Mail, MapPin, Meh, Phone, Play, PlusCircle, Share2, Smile, SmilePlus, Tag, ThumbsUp, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

// Hardcoded status options (should match backend JobStatus and UI needs)
// Consider fetching these from backend or a shared config if they become dynamic
const JOB_STATUSES_OPTIONS = [
  { value: 'interested', label: 'Interested' },
  { value: 'applied', label: 'Applied' },
  { value: 'screening', label: 'Screening' },
  { value: 'technical', label: 'Technical Interview' },
  { value: 'onsite', label: 'On-site Interview' },
  { value: 'takehome', label: 'Take-home Assignment' },
  { value: 'pending_offer', label: 'Pending Offer' },
  { value: 'offer_received', label: 'Offer Received' },
  { value: 'offer_accepted', label: 'Offer Accepted' },
  { value: 'offer_declined', label: 'Offer Declined' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'withdrawn', label: 'Withdrawn' },
  { value: 'ghosted', label: 'Ghosted' },
];

export default function JobDetails() {
  // Edit note modal state
  const [editNoteModalOpen, setEditNoteModalOpen] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteValue, setEditingNoteValue] = useState('');

  // Save edited note
  const handleEditNoteSave = async () => {
    if (!jobId || !editingNoteId || !editingNoteValue.trim()) return;
    try {
      await api.updateJobNote(jobId, editingNoteId, { note: editingNoteValue });
      toast.success('Note updated');
      setEditNoteModalOpen(false);
      setEditingNoteId(null);
      setEditingNoteValue('');
      fetchData();
    } catch (e: any) {
      toast.error(getUserErrorMessage(e, 'Unable to update note. Please try again later.'));
    }
  };

  // Delete a status (except current)
  const handleDeleteStatus = async (statusId: string) => {
    if (!jobId || !statusId) return;
    try {
      await api.deleteJobStatus(jobId, statusId);
      toast.success('Status deleted');
      fetchData();
    } catch (e: any) {
      toast.error(getUserErrorMessage(e, 'Unable to delete status. Please try again later.'));
    }
  };

  // Edit modal state for job basics
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [jobData, setJobData] = useState<JobCompleteApiResponse | null>(null);
  const [editJob, setEditJob] = useState({
    title: '',
    salary: '',
    location: '',
    remote: '',
    listing_url: '',
    raw_posting: '',
  });

  useEffect(() => {
    if (jobData) {
      setEditJob({
        title: jobData.title || '',
        salary: jobData.salary || '',
        location: jobData.location || '',
        remote: jobData.remote || '',
        listing_url: jobData.listing_url || '',
        raw_posting: jobData.raw_posting || '',
      });
    }
  }, [jobData]);

  const handleEditJobSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!jobId) return;
    setLoading(true);
    try {
      // Only send updatable fields
      const updatePayload = {
        title: editJob.title,
        salary: editJob.salary,
        location: editJob.location,
        remote: editJob.remote,
        listing_url: editJob.listing_url,
        raw_posting: editJob.raw_posting,
      };
      await api.updateJob(jobId, updatePayload);
      // Refresh job data
      const updated = await api.getJobCompleteById(jobId);
      setJobData(updated);
      setEditModalOpen(false);
    } catch (err: any) {
      toast.error(getUserErrorMessage(err, 'Unable to update job. Please try again later.'));
    } finally {
      setLoading(false);
    }
  };


  const { api } = useApiClient();
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusUpdating, setStatusUpdating] = useState(false);

  // Job analysis state
  const [jobAnalysis, setJobAnalysis] = useState<JobAnalysisApiResponse | null>(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisState, setAnalysisState] = useState<'none' | 'not_generated' | 'generating' | 'ready'>('none');
  const [generatingAnalysis, setGeneratingAnalysis] = useState(false);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [isSubscribed, setIsSubscribed] = useState<boolean>(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);

  // States for new UI elements (example: for adding a note)
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);

  // States for person notes
  const [editingPersonId, setEditingPersonId] = useState<string | null>(null);
  const [currentPersonNotes, setCurrentPersonNotes] = useState<Array<{ type: string, value: string }>>([]);
  const [newNoteType, setNewNoteType] = useState<string>('note');
  const [newNoteValue, setNewNoteValue] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedSentiment, setSelectedSentiment] = useState<number>(3);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState<'person' | 'interview' | null>(null);
  const [currentPersonName, setCurrentPersonName] = useState('');
  const [currentPersonRole, setCurrentPersonRole] = useState('');
  const [currentInterviewDate, setCurrentInterviewDate] = useState<Date | undefined>(undefined);
  const [currentInterviewNotes, setCurrentInterviewNotes] = useState('');
  const [currentInterviewRating, setCurrentInterviewRating] = useState(0);
  const [currentInterviewPeople, setCurrentInterviewPeople] = useState<string[]>([]);
  const [isSubmittingModal, setIsSubmittingModal] = useState(false);
  const [editingInterviewId, setEditingInterviewId] = useState<string | null>(null);
  const [currentInterviewTime, setCurrentInterviewTime] = useState<string>("12:00"); // Default time e.g., "10:00"

  const RATING_LEVELS = [
    { value: 1, label: 'terrible', IconComponent: Angry },
    { value: 2, label: 'bad', IconComponent: Frown },
    { value: 3, label: 'neutral', IconComponent: Meh },
    { value: 4, label: 'good', IconComponent: Smile },
    { value: 5, label: 'great', IconComponent: SmilePlus },
  ] as const;

  const RATING_TEXT_COLORS = [
    'text-red-600',    // Corresponds to rating 1 (Terrible)
    'text-orange-500', // Corresponds to rating 2 (Bad)
    'text-yellow-500', // Corresponds to rating 3 (Neutral)
    'text-lime-500',   // Corresponds to rating 4 (Good)
    'text-green-600',  // Corresponds to rating 5 (Great)
  ];

  // Note type options with their associated icons
  const NOTE_TYPES = [
    { value: 'note', label: 'Note', icon: 'FileText' },
    { value: 'linkedin', label: 'LinkedIn', icon: 'Linkedin' },
    { value: 'email', label: 'Email', icon: 'Mail' },
    { value: 'phone', label: 'Phone', icon: 'Phone' },
    { value: 'website', label: 'Website/Portfolio', icon: 'Globe' },
    { value: 'date', label: 'Interaction Date', icon: 'Calendar' },
    { value: 'sentiment', label: 'Sentiment', icon: 'ThumbsUp' },
    { value: 'social', label: 'Social Media', icon: 'Share2' },
    { value: 'tags', label: 'Tags', icon: 'Tag' },
  ];

  // Predefined tags for people
  const PERSON_TAGS = [
    'friendly',
    'technical',
    'gatekeeper',
    'hiring manager',
    'potential coworker',
    'aggressive',
    'helpful',
    'decision maker',
    'recruiter',
    'HR',
    'senior leadership',
    'team lead',
    'engineer',
    'designer',
    'product manager'
  ];

  const openModal = (contentType: 'person' | 'interview', personOrInterviewToEdit?: JobCompleteApiResponse['job_interviews'][0] | JobCompleteApiResponse['persons'][0]) => {
    setModalContent(contentType);
    setEditingInterviewId(null);
    setEditingPersonId(null);

    // Reset common fields
    setCurrentPersonName('');
    setCurrentPersonRole('');
    setCurrentInterviewDate(undefined);
    setCurrentInterviewTime("12:00");
    setCurrentInterviewNotes('');
    setCurrentInterviewRating(0); // Default to 0 or a specific unrated value
    setCurrentInterviewPeople([]);
    setCurrentPersonNotes([]);
    setNewNoteType('note');
    setNewNoteValue('');
    setSelectedDate(undefined);
    setSelectedSentiment(3);
    setSelectedTags([]);

    if (contentType === 'interview' && personOrInterviewToEdit && 'interview_date' in personOrInterviewToEdit) {
      const interviewToEdit = personOrInterviewToEdit;
      setEditingInterviewId(interviewToEdit.id);
      // Parse the ISO string from backend (which is in UTC) and convert to local timezone
      const interviewDateObj = new Date(interviewToEdit.interview_date);
      setCurrentInterviewDate(interviewDateObj);
      // Use getHours() and getMinutes() which return local timezone values
      setCurrentInterviewTime(`${interviewDateObj.getHours().toString().padStart(2, '0')}:${interviewDateObj.getMinutes().toString().padStart(2, '0')}`);
      setCurrentInterviewNotes(interviewToEdit.notes || '');
      setCurrentInterviewRating(interviewToEdit.rating || 0);
      // Assuming interviewToEdit.people is an array of person IDs (strings)
      // If it's an array of person objects, map to IDs
      setCurrentInterviewPeople(interviewToEdit.people?.map(p => typeof p === 'string' ? p : p.id) || []);
    } else if (contentType === 'person' && personOrInterviewToEdit && 'name' in personOrInterviewToEdit) {
      const personToEdit = personOrInterviewToEdit;
      setEditingPersonId(personToEdit.id);
      setCurrentPersonName(personToEdit.name);
      setCurrentPersonRole(personToEdit.role || '');

      // Process notes if they exist
      if (personToEdit.notes && personToEdit.notes.length > 0) {
        const formattedNotes = personToEdit.notes.map(note => ({
          id: note.id,
          type: note.note_type,
          value: note.note
        }));
        setCurrentPersonNotes(formattedNotes);
      }
    }

    setIsModalOpen(true);
  };

  const handleModalSubmit = async () => {
    if (!jobId) return;
    setIsSubmittingModal(true);
    try {
      if (modalContent === 'person') {
        if (!currentPersonName.trim()) {
          toast.error('Person name is required.');
          setIsSubmittingModal(false);
          return;
        }

        // Create or update person
        if (editingPersonId) {
          // Update existing person basic info
          await api.updateJobPerson(jobId, editingPersonId, {
            name: currentPersonName,
            role: currentPersonRole || undefined,
            notes: currentPersonNotes.map(note => ({
              ...(note.id ? { id: note.id } : {}),
              note_type: note.type,
              note: note.value
            }))
          });

          toast.success('Person updated');
        } else {
          // Create new person
          const personRes = await api.createJobPerson(jobId, {
            name: currentPersonName,
            role: currentPersonRole || undefined,
            notes: currentPersonNotes.map(note => ({
              note_type: note.type,
              note: note.value
            }))
          });

          toast.success('Person added');
        }
      } else if (modalContent === 'interview') {
        if (!currentInterviewDate || !currentInterviewTime) {
          toast.error('Date and Time are required for interviews.');
          setIsSubmittingModal(false);
          return;
        }

        const [hours, minutes] = currentInterviewTime.split(':').map(Number);
        // Create date in user's local timezone
        const localDateTime = new Date(
          currentInterviewDate.getFullYear(),
          currentInterviewDate.getMonth(),
          currentInterviewDate.getDate(),
          hours,
          minutes
        );
        // Convert to UTC ISO string - this preserves the actual moment in time
        // The database will store this as UTC, and we'll convert back to local time when displaying
        const payload = {
          interview_date: localDateTime.toISOString(),
          notes: currentInterviewNotes,
          rating: currentInterviewRating,
          people: currentInterviewPeople,
        };

        if (editingInterviewId) {
          const updatedInterview = await api.updateJobInterview(jobId, editingInterviewId, payload);
          toast.success('Interview updated');
          // Manually update local state to reflect mock update immediately
          setJobData(prevData => {
            if (!prevData) return prevData;
            const updatedInterviews = prevData.job_interviews.map(interview =>
              interview.id === editingInterviewId ? { ...interview, ...updatedInterview } : interview
            );
            return { ...prevData, job_interviews: updatedInterviews };
          });
        } else {
          await api.createJobInterview(jobId, payload);
          toast.success('Interview added');
        }
      }
      setIsModalOpen(false);
      fetchData(); // Refresh data to show changes
    } catch (e: any) {
      const action = editingInterviewId || editingPersonId ? 'update' : 'add';
      toast.error(
        getUserErrorMessage(e, `Unable to ${action} ${modalContent}. Please try again later.`)
      );
    } finally {
      setIsSubmittingModal(false);
    }
  };

  // Function to handle adding a note to a person
  const handleAddPersonNote = async () => {
    if (!newNoteType || !newNoteValue.trim()) {
      toast.error('Both note type and value are required');
      return;
    }

    // Create a new note object
    const newNote = {
      type: newNoteType,
      value: newNoteValue
    };

    // Add to current notes
    setCurrentPersonNotes([...currentPersonNotes, newNote]);

    // Reset form
    setNewNoteType('note');
    setNewNoteValue('');
    setSelectedDate(undefined);
    setSelectedSentiment(3);
    setSelectedTags([]);
  };

  // Function to remove a note
  const handleRemovePersonNote = (index: number) => {
    const updatedNotes = [...currentPersonNotes];
    updatedNotes.splice(index, 1);
    setCurrentPersonNotes(updatedNotes);
  };

  // Function to handle deleting a person
  const handleDeletePerson = async () => {
    if (!jobId || !editingPersonId) return;

    try {
      await api.deleteJobPerson(jobId, editingPersonId);
      toast.success('Person deleted');
      setIsModalOpen(false);
      fetchData();
    } catch (e: any) {
      toast.error(getUserErrorMessage(e, 'Unable to delete person. Please try again later.'));
    }
  };

  // Function to handle deleting an interview
  const handleDeleteInterview = async () => {
    if (!jobId || !editingInterviewId) return;
    try {
      await api.deleteJobInterview(jobId, editingInterviewId);
      toast.success('Interview deleted');
      setIsModalOpen(false);
      fetchData();
    } catch (e: any) {
      toast.error(getUserErrorMessage(e, 'Unable to delete interview. Please try again later.'));
    }
  };

  const fetchJobAnalysis = async () => {
    if (!jobId) return;
    setAnalysisLoading(true);
    try {
      const analysis = await api.getJobAnalysis(jobId);
      setJobAnalysis(analysis);
      setAnalysisState('ready');
    } catch (e: any) {
      // If analysis doesn't exist (404), look at detail for next steps
      if (e.statusCode === 404) {
        const msg = (e.message || '').toLowerCase();
        if (msg.includes('still generating')) {
          setAnalysisState('generating');
        } else if (msg.includes('not generated')) {
          setAnalysisState('not_generated');
        } else {
          setAnalysisState('none');
        }
      } else {
        console.error('Error loading job analysis:', e);
      }
    } finally {
      setAnalysisLoading(false);
    }
  };

  // Fetch subscription
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const res = await api.getMySubscription();
        // Consider user subscribed if a subscription record exists and status isn't clearly canceled
        const active = !!res.subscription && (res.subscription.status ? res.subscription.status !== 'canceled' : true);
        setIsSubscribed(active);
      } catch (e) {
        console.error('Failed to load subscription:', e);
        setIsSubscribed(false);
      } finally {
        setSubscriptionLoading(false);
      }
    };
    fetchSubscription();
  }, [api]);

  const handleGenerateAnalysis = async () => {
    if (!jobId) return;
    setGeneratingAnalysis(true);
    setAnalysisLoading(true);
    try {
      await api.generateResumeAnalysis(jobId);
      toast.success('Started resume analysis. This may take up to a minute.');
      // Polling effect below will continue to try fetching until analysis is ready
      // Kick an immediate fetch attempt
      setAnalysisState('generating');
      fetchJobAnalysis();
    } catch (e: any) {
      if (e.statusCode === 405) {
        // Not allowed for current subscription - show premium modal
        setShowPremiumModal(true);
      } else {
        toast.error(
          getUserErrorMessage(
            e,
            'Unable to start analysis right now. Please try again later.'
          )
        );
      }
    } finally {
      setGeneratingAnalysis(false);
    }
  };

  // Polling for job analysis: only when generating
  useEffect(() => {
    if (analysisState === 'generating' && !jobAnalysis && !analysisLoading && jobId) {
      let cancelled = false;
      const poll = async () => {
        if (cancelled) return;
        try {
          const analysis = await api.getJobAnalysis(jobId);
          setJobAnalysis(analysis);
          setAnalysisState('ready');
        } catch (e: any) {
          if (e.statusCode === 404) {
            const msg = (e.message || '').toLowerCase();
            if (msg.includes('still generating')) {
              // keep polling
              setTimeout(poll, 5000);
            } else if (msg.includes('not generated')) {
              // task disappeared or not started; stop polling and show action
              setAnalysisState('not_generated');
            } else {
              // unknown 404 detail, retry later
              setTimeout(poll, 5000);
            }
          }
        }
      };
      const timeoutId = setTimeout(poll, 2000);
      return () => { cancelled = true; clearTimeout(timeoutId); };
    }
  }, [analysisState, jobAnalysis, analysisLoading, jobId, api]);

  const fetchInterviews = async () => {
    // Refresh the entire job data to get updated interviews
    await fetchData();
  };

  const handleInterviewsUpdate = (updatedInterviews: InterviewResponse[]) => {
    // Smoothly update only the interviews without full page refresh
    setJobData(prevData => prevData ? {
      ...prevData,
      interviews: updatedInterviews
    } : null);
  };

  const fetchData = async () => {
    if (!jobId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.getJobCompleteById(jobId);
      setJobData(data);

      // If analysis is already available on the job, use it
      if (data.analysis) {
        setJobAnalysis(data.analysis);
      } else {
        // Otherwise, try to fetch it separately
        fetchJobAnalysis();
      }

      // Interviews are now included in the job data, no need to fetch separately
    } catch (e: any) {
      const message = getUserErrorMessage(
        e,
        'Unable to load job details right now. Please try again later.'
      );
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Targeted function to fetch only generation status without causing page re-renders
  const fetchGenerationStatus = async () => {
    if (!jobId || !jobData) return;
    try {
      const statusResponse = await api.getJobGenerationStatus(jobId);
      // Only update the generation_status field to avoid full re-render
      setJobData(prevData => prevData ? {
        ...prevData,
        generation_status: statusResponse.status
      } : null);
    } catch (e: any) {
      // Silently fail for status checks to avoid spamming user with errors
      console.error('Error fetching generation status:', e);
    }
  };

  useEffect(() => {
    fetchData();
  }, [jobId, api]);

  const handleStatusChange = async (newStatusValue: string) => {
    if (!jobId || !newStatusValue) return;
    setStatusUpdating(true);
    try {
      // Assuming your API has an endpoint like this, or adjust as needed
      // The api.ts was updated to include `updateJobStatus`
      await api.addJobStatus(jobId, newStatusValue);
      toast.success('Status updated');
      // Refresh data to get the latest status and history
      fetchData();
    } catch (e: any) {
      toast.error(getUserErrorMessage(e, 'Unable to update status. Please try again later.'));
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleAddNote = async () => {
    if (!jobId || !newNote.trim()) return;
    setAddingNote(true);
    try {
      await api.createJobNote(jobId, { note: newNote });
      toast.success('Note added');
      setNewNote('');
      fetchData();
    } catch (e: any) {
      toast.error(getUserErrorMessage(e, 'Unable to add note. Please try again later.'));
    } finally {
      setAddingNote(false);
    }
  };

  if (loading) return <DashboardLayout><div className="p-8 text-center">Loading...</div></DashboardLayout>;
  if (error) return <DashboardLayout><div className="p-8 text-center text-red-500">Error: {error}</div></DashboardLayout>;
  if (!jobData) return <DashboardLayout><div className="p-8 text-center text-red-500">Job not found.</div></DashboardLayout>;

  // Helper: get sorted statuses (latest first)
  const sortedStatuses = jobData.statuses
    ? [...jobData.statuses].sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
    : [];
  const currentStatus = sortedStatuses.length > 0 ? sortedStatuses[0].status : 'interested';

  return (
    <DashboardLayout>
      <div className="space-y-0 sm:space-y-6 -m-4 sm:m-0 p-0 sm:p-6">
        {/* Header Section */}
        <div className="mb-6 px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
            {/* Desktop layout with back button */}
            <div className="hidden sm:flex items-center w-full sm:w-auto gap-4">
              <Button variant="outline" onClick={() => navigate('/jobs')} className="shrink-0">
                <ChevronLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
              <div>
                <h1 className="text-3xl font-bold text-gray-800 leading-tight">{jobData.title}</h1>
                <p className="text-xl text-gray-600">{jobData.company}</p>
              </div>
            </div>

            {/* Mobile layout - centered title, no back button */}
            <div className="flex sm:hidden flex-col items-center w-full text-center">
              <h1 className="text-2xl font-bold text-gray-800 leading-tight">{jobData.title}</h1>
              <p className="text-lg text-gray-600">{jobData.company}</p>
            </div>
          </div>

          {/* Details section - centered on mobile */}
          <div className="mt-4 flex flex-col sm:flex-row sm:flex-wrap gap-x-6 gap-y-2 text-sm text-gray-500 items-center sm:items-start sm:justify-start justify-center text-center sm:text-left">
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-x-6 gap-y-2 items-center">
              {jobData.location && (
                <span className="flex items-center">
                  <MapPin className="mr-1.5 h-4 w-4" />
                  {jobData.location}
                </span>
              )}
              {jobData.salary && <span className="flex items-center"><DollarSign className="mr-1.5 h-4 w-4" /> {jobData.salary}</span>}
              {jobData.remote && <span className="flex items-center"><Briefcase className="mr-1.5 h-4 w-4" /> {jobData.remote ? jobData.remote.charAt(0).toUpperCase() + jobData.remote.slice(1) : ''}</span>}
              {jobData.listing_url && (
                <a href={jobData.listing_url} target="_blank" rel="noopener noreferrer">
                  <span className="flex items-center">
                    <Link className="mr-2 h-4 w-4" /> Listing
                  </span>
                </a>
              )}
              <span className="flex items-center"><CalendarDaysIcon className="mr-1.5 h-4 w-4" /> Added: {new Date(jobData.created_at).toLocaleDateString()}</span>
            </div>
          </div>

          {/* Mobile buttons - underneath details, side by side */}
          <div className="mt-4 flex sm:hidden gap-2 justify-center">
            <Button variant="outline" onClick={() => navigate('/jobs')} className="flex-1">
              <ChevronLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            <Button variant="outline" onClick={() => setEditModalOpen(true)} className="flex-1">
              <Edit3 className="mr-2 h-4 w-4" />
              Edit
            </Button>
          </div>

          {/* Desktop edit button - hidden on mobile */}
          <div className="hidden sm:flex justify-end mt-2">
            <button
              className="p-2 rounded-full hover:bg-gray-200 transition-colors text-gray-600 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-300"
              title="Edit job"
              onClick={() => setEditModalOpen(true)}
              type="button"
            >
              <Edit3 className="h-5 w-5" />
            </button>
          </div>

          {/* Edit Job Modal */}
          {editModalOpen && (
            <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
              <DialogContent className="w-[100vw] sm:w-full sm:max-w-md mx-0 sm:mx-auto">
                <DialogHeader>
                  <DialogTitle>Edit Job</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleEditJobSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="job-title">Job Title</Label>
                    <Input id="job-title" value={editJob.title} onChange={e => setEditJob({ ...editJob, title: e.target.value })} />
                  </div>
                  <div>
                    <Label htmlFor="job-location">Location</Label>
                    <Input id="job-location" value={editJob.location} onChange={e => setEditJob({ ...editJob, location: e.target.value })} />
                  </div>
                  <div>
                    <Label htmlFor="job-remote">Remote Status</Label>
                    <select
                      id="job-remote"
                      value={editJob.remote}
                      onChange={e => setEditJob({ ...editJob, remote: e.target.value })}
                      className="block w-full border rounded px-3 py-2"
                      required
                    >
                      <option value="">Select...</option>
                      <option value="on-site">On-site</option>
                      <option value="hybrid">Hybrid</option>
                      <option value="remote">Remote</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="job-listing-url">Listing URL</Label>
                    <Input id="job-listing-url" value={editJob.listing_url} onChange={e => setEditJob({ ...editJob, listing_url: e.target.value })} />
                  </div>
                  <div>
                    <Label htmlFor="job-listing">Listing (Raw Posting)</Label>
                    <Textarea id="job-listing" value={editJob.raw_posting} onChange={e => setEditJob({ ...editJob, raw_posting: e.target.value })} />
                  </div>
                  <div className="flex justify-end gap-2">
                    <button type="button" className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300" onClick={() => setEditModalOpen(false)}>
                      Cancel
                    </button>
                    <button type="submit" className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700">
                      Save
                    </button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}

        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 sm:gap-6">
          {/* Left Column (2/3 width on md+) */}
          <div className="md:col-span-2 space-y-0 sm:space-y-6">
            {/* Description Card */}
            <Card className="bg-white shadow-none sm:shadow-lg rounded-none sm:rounded-lg mx-0">
              <CardHeader><CardTitle>Description</CardTitle></CardHeader>
              <CardContent>
                <div className="whitespace-pre-line text-gray-700 max-h-96 overflow-y-auto">
                  {jobData.description || jobData.raw_posting || 'No description provided.'}
                </div>
              </CardContent>
            </Card>

            {/* Job Analysis Card - unified component for preview/generate/generating */}
            {(!subscriptionLoading) && (
              <JobAnalysis
                analysis={jobAnalysis}
                state={jobAnalysis ? 'ready' : analysisState}
                isSubscribed={isSubscribed}
                onGetPremium={() => navigate('/subscription')}
                onGenerate={handleGenerateAnalysis}
                generating={generatingAnalysis || analysisState === 'generating'}
              />
            )}
            {subscriptionLoading && (analysisLoading || analysisState === 'generating') && (
              <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
                {/* Header - Loading State */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 sm:p-6 border-b border-gray-200">
                  <div className="flex items-center gap-3">
                    <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-2 border-blue-600 border-t-transparent"></div>
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Analyzing Listing</h2>
                    <div className="ml-auto bg-blue-100 text-blue-800 border-blue-200 font-semibold text-sm sm:text-base px-3 py-1 rounded-full animate-pulse">
                      Processing...
                    </div>
                  </div>
                </div>

                {/* Tab Navigation - Loading State */}
                <div className="border-b border-gray-200 bg-gray-50">
                  <div className="flex overflow-x-auto">
                    {[
                      { label: 'Description', shortLabel: 'Info' },
                      { label: 'Competitive Analysis', shortLabel: 'Analysis' },
                      { label: 'Hiring Manager Thoughts', shortLabel: 'Manager' },
                      { label: 'Recommendations', shortLabel: 'Tips' }
                    ].map((tab, index) => (
                      <div
                        key={index}
                        className={`flex items-center gap-2 px-4 sm:px-6 py-3 sm:py-4 text-sm sm:text-base font-medium whitespace-nowrap ${index === 0 ? 'text-blue-600 border-b-2 border-blue-600 bg-white' : 'text-gray-400'
                          }`}
                      >
                        <div className="w-4 h-4 sm:w-5 sm:h-5 bg-gray-300 rounded animate-pulse"></div>
                        <span className="hidden sm:inline">{tab.label}</span>
                        <span className="sm:hidden">{tab.shortLabel}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Content - Loading State */}
                <div className="p-4 sm:p-6">
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 sm:p-6 border-l-4 border-blue-500">
                    <div className="flex items-center mb-3">
                      <div className="w-5 h-5 sm:w-6 sm:h-6 bg-blue-300 rounded animate-pulse mr-2"></div>
                      <div className="h-5 sm:h-6 bg-blue-300 rounded animate-pulse w-48"></div>
                    </div>
                    <div className="space-y-3">
                      <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                        🤖 Our AI is carefully analyzing your resume against this job posting to provide personalized insights...
                      </p>
                      <div className="space-y-2">
                        <div className="h-4 bg-blue-200 rounded animate-pulse"></div>
                        <div className="h-4 bg-blue-200 rounded animate-pulse w-4/5"></div>
                        <div className="h-4 bg-blue-200 rounded animate-pulse w-3/5"></div>
                      </div>
                      <div className="mt-4 p-3 bg-white/80 rounded border border-blue-200">
                        <p className="text-xs sm:text-sm text-blue-700 italic">
                          💡 This usually takes 30-60 seconds. We're evaluating your competitive advantages, areas for improvement, and what hiring managers might think.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Interview Section */}
            <InterviewSection
              jobId={jobId!}
              interviews={jobData.interviews || []}
              onInterviewCreated={fetchInterviews}
              onInterviewsUpdate={handleInterviewsUpdate}
            />

            {/* Pitch Section */}
            <Card className="mb-8">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <h2 className="text-lg sm:text-2xl lg:text-3xl font-bold leading-tight">
                  Pitch Practice
                </h2>
                <Button size="sm" onClick={() => navigate(`/pitch/new?jobId=${jobId}`)}>
                  New
                </Button>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="mb-2 text-gray-600 text-sm">
                  Sometimes the toughest interview question is "Tell me about yourself". Pitches is how the Coach teaches you how to present and talk about yourself.
                </div>
                <PitchList jobId={jobId!} />
              </CardContent>
            </Card>

            {/* Salary Negotiation Section */}
            {jobId && (
              <Card className="mb-8">
                <CardHeader className="pb-2 flex flex-row items-center justify-between">
                  <h2 className="text-lg sm:text-2xl lg:text-3xl font-bold leading-tight">
                    Salary Negotiation
                  </h2>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="mb-4 text-gray-600 text-sm">
                    Run a realistic salary negotiation for this job, then get detailed,
                    actionable feedback on how you did.
                  </div>
                  <div
                    onClick={() => navigate(`/salary-negotiations?jobId=${jobId}`)}
                    className="group relative overflow-hidden rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 cursor-pointer transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-[1.02] p-6 flex items-center justify-center"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform flex-shrink-0">
                        <Play className="w-8 h-8 text-white fill-white" />
                      </div>
                      <div className="text-center">
                        <h3 className="text-xl font-bold text-white mb-1">Practice Salary Negotiation</h3>
                        <p className="text-blue-100 text-sm">Get detailed feedback on your approach</p>
                      </div>
                    </div>
                  </div>
                  <div className="border-t pt-3">
                    <SalaryNegotiationList jobId={jobId} showTitle={true} />
                  </div>
                </CardContent>
              </Card>
            )}


            {/* Edit Note Modal */}
            <Dialog open={editNoteModalOpen} onOpenChange={setEditNoteModalOpen}>
              <DialogContent className="w-[100vw] sm:w-full sm:max-w-md mx-0 sm:mx-auto">
                <DialogHeader>
                  <DialogTitle>Edit Note</DialogTitle>
                </DialogHeader>
                <Textarea
                  value={editingNoteValue}
                  onChange={e => setEditingNoteValue(e.target.value)}
                  className="mb-4"
                  autoFocus
                />
                <div className="flex gap-2 justify-end">
                  <Button variant="destructive" onClick={async () => {
                    if (!jobId || !editingNoteId) return;
                    try {
                      await api.deleteJobNote(jobId, editingNoteId);
                      toast.success('Note deleted');
                      setEditNoteModalOpen(false);
                      setEditingNoteId(null);
                      setEditingNoteValue('');
                      fetchData();
                    } catch (e: any) {
                      toast.error(getUserErrorMessage(e, 'Unable to delete note. Please try again later.'));
                    }
                  }}>Delete</Button>
                  <Button variant="outline" onClick={() => setEditNoteModalOpen(false)}>Cancel</Button>
                  <Button onClick={handleEditNoteSave} disabled={!editingNoteValue.trim()}>Save</Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Notes Card */}
            <Card className="bg-white shadow-none sm:shadow-lg rounded-none sm:rounded-lg mx-0">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Notes</CardTitle>
                {/* <Button size="sm" variant="outline"><PlusCircle className="mr-2 h-4 w-4" /> Add Note</Button> */}
              </CardHeader>
              <CardContent>
                <div className="space-y-3 mb-4">
                  {jobData.notes && jobData.notes.length > 0 ? (
                    jobData.notes.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()).map(note => (
                      <div key={note.id} className="p-3 border rounded-md bg-gray-50 flex justify-between items-start">
                        <div>
                          <p className="text-sm text-gray-700">{note.note}</p>
                          <p className="text-xs text-gray-400 mt-1">{new Date(note.created_at).toLocaleString()}</p>
                        </div>
                        <button
                          className="ml-2 text-blue-500 hover:text-blue-700"
                          title="Edit note"
                          onClick={() => {
                            setEditingNoteId(note.id);
                            setEditingNoteValue(note.note);
                            setEditNoteModalOpen(true);
                          }}
                        >
                          <Edit3 size={16} />
                        </button>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500">No notes yet.</p>
                  )}
                </div>
                <div className="mt-4 pt-4 border-t">
                  <Textarea
                    placeholder="Add a new note..."
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    className="mb-2"
                  />
                  <Button onClick={handleAddNote} disabled={addingNote || !newNote.trim()}>
                    {addingNote ? 'Adding...' : <><PlusCircle className="mr-2 h-4 w-4" /> Add Note</>}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Modal for Adding Person/Interview */}
          <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
            <DialogContent className="w-[100vw] sm:w-full sm:max-w-[425px] mx-0 sm:mx-auto">
              <DialogHeader>
                <DialogTitle>{editingInterviewId ? 'Edit' : 'Add'} {modalContent === 'person' ? 'Person' : 'Interview'}</DialogTitle>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                {modalContent === 'person' && (
                  <>
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="personName" className="text-right">
                        Name
                      </Label>
                      <Input id="personName" value={currentPersonName} onChange={(e) => setCurrentPersonName(e.target.value)} className="col-span-3" placeholder="Contact's full name" />
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="personRole" className="text-right">
                        Role
                      </Label>
                      <Input id="personRole" value={currentPersonRole} onChange={(e) => setCurrentPersonRole(e.target.value)} className="col-span-3" placeholder="e.g., Hiring Manager" />
                    </div>
                  </>
                )}
                {modalContent === 'interview' && (
                  <>
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="interviewDate" className="text-right">
                        Date
                      </Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant={"outline"}
                            className={`col-span-3 justify-start text-left font-normal ${!currentInterviewDate && "text-muted-foreground"}`}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {currentInterviewDate ? currentInterviewDate.toLocaleDateString() : <span>Pick a date</span>}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={currentInterviewDate}
                            onSelect={(date) => {
                              if (date) {
                                const [hours, minutes] = currentInterviewTime.split(':').map(Number);
                                const newDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), hours, minutes);
                                setCurrentInterviewDate(newDate);
                              } else {
                                setCurrentInterviewDate(undefined);
                              }
                            }}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="interviewTime" className="text-right">
                        Time
                      </Label>
                      <Input
                        id="interviewTime"
                        type="time"
                        value={currentInterviewTime}
                        onChange={(e) => {
                          setCurrentInterviewTime(e.target.value);
                          if (currentInterviewDate) {
                            const [hours, minutes] = e.target.value.split(':').map(Number);
                            const newDate = new Date(currentInterviewDate.getFullYear(), currentInterviewDate.getMonth(), currentInterviewDate.getDate(), hours, minutes);
                            setCurrentInterviewDate(newDate);
                          }
                        }}
                        className="col-span-3"
                      />
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="interviewNotes" className="text-right">
                        Notes
                      </Label>
                      <Textarea id="interviewNotes" value={currentInterviewNotes} onChange={(e) => setCurrentInterviewNotes(e.target.value)} className="col-span-3" placeholder="Interview details, topics, feelings..." rows={4} />
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="interviewRating" className="text-right">
                        Rating
                      </Label>
                      <div className="col-span-3 flex space-x-2 items-center">
                        {RATING_LEVELS.map(level => (
                          <div
                            key={level.value}
                            onClick={() => setCurrentInterviewRating(level.value)}
                            title={level.label.charAt(0).toUpperCase() + level.label.slice(1)}
                            className={`p-2 rounded-md cursor-pointer flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-700 ${currentInterviewRating === level.value ? 'bg-gray-200 dark:bg-gray-600 ring-2 ring-offset-1 ring-gray-400' : ''}`}
                          >
                            <level.IconComponent className={`h-8 w-8 ${RATING_TEXT_COLORS[level.value - 1]}`} />
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
              <DialogFooter className="flex flex-col sm:flex-row items-center justify-between gap-2 pt-6">
                <div className="flex gap-2 w-full">
                  <div className="flex gap-2 flex-1">
                    {modalContent === 'person' && editingPersonId && (
                      <Button variant="destructive" onClick={handleDeletePerson} className="flex-1"><Trash2 className="mr-2 h-4 w-4" /></Button>
                    )}
                    {modalContent === 'interview' && editingInterviewId && (
                      <Button variant="destructive" onClick={handleDeleteInterview} className="flex-1"><Trash2 className="mr-2 h-4 w-4" /></Button>
                    )}
                    <Button type="submit" onClick={handleModalSubmit} disabled={isSubmittingModal} className="flex-1">
                      {isSubmittingModal ? 'Saving...' : 'Save'}
                    </Button>
                  </div>
                </div>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Right Column (1/3 width on md+) */}
          <div className="space-y-0 sm:space-y-6">
            {/* Job Todos Card (embedded) */}
            {jobId && (
              <JobTodoCard jobId={jobId} />
            )}
            {/* Status Card */}
            <Card className="bg-white shadow-none sm:shadow-lg rounded-none sm:rounded-lg mx-0">
              <CardHeader><CardTitle>Status</CardTitle></CardHeader>
              <CardContent>
                <div className="mb-4">
                  <div className="mb-2">
                    <JobApplicationStatusBadge status={currentStatus} size="md" />
                  </div>
                  <Select onValueChange={handleStatusChange} value={currentStatus} disabled={statusUpdating}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Update status..." />
                    </SelectTrigger>
                    <SelectContent>
                      {JOB_STATUSES_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>
                          <JobApplicationStatusBadge status={opt.value} />
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {statusUpdating && <p className="text-sm text-gray-500 mt-1">Updating...</p>}
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-2 text-gray-600">History:</h4>
                  <ul className="space-y-1 text-xs">
                    {jobData.statuses && jobData.statuses.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()).map((s, idx) => (
                      <div key={s.id} className="flex items-center gap-2 text-sm">
                        <JobApplicationStatusBadge status={s.status} />
                        <span className="text-gray-600">- {new Date(s.updated_at).toLocaleDateString()}</span>
                        {idx !== 0 && (
                          <button
                            className="ml-2 text-red-500 hover:text-red-700"
                            title="Delete this status"
                            onClick={async () => {
                              await handleDeleteStatus(s.id);
                            }}
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Upcoming Interviews Card */}
            <Card className="bg-white shadow-none sm:shadow-lg rounded-none sm:rounded-lg mx-0">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Upcoming Interviews</CardTitle>
                <Button size="sm" variant="outline" onClick={() => openModal('interview')}><PlusCircle className="mr-2 h-4 w-4" /> Add</Button>
              </CardHeader>
              <CardContent>
                {jobData.job_interviews && jobData.job_interviews.length > 0 ? (
                  jobData.job_interviews.sort((a, b) => new Date(a.interview_date).getTime() - new Date(b.interview_date).getTime()).map(interview => (
                    <div key={interview.id} className="p-3 border rounded-md bg-gray-50 mb-2 cursor-pointer hover:bg-gray-100 transition-colors relative group" onClick={() => openModal('interview', interview)}>
                      <Button variant="ghost" size="sm" className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => { e.stopPropagation(); openModal('interview', interview); }}>
                        <Edit3 className="h-4 w-4" />
                      </Button>
                      <p className="text-sm font-semibold text-gray-800 pr-8">{new Date(interview.interview_date).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}</p>
                      <p className="text-xs text-gray-600 mt-1">
                        {interview.notes && interview.notes.length > 70 ? `${interview.notes.substring(0, 70)}...` : interview.notes || ''}
                      </p>
                      {/* Display rating face icon if available */}
                      {interview.rating !== null && interview.rating >= 1 && interview.rating <= 5 &&
                        (() => {
                          const ratingLevel = RATING_LEVELS.find(rl => rl.value === interview.rating);
                          if (!ratingLevel) return null;
                          const Icon = ratingLevel.IconComponent;
                          return (
                            <div className="flex items-center mt-1" title={ratingLevel.label.charAt(0).toUpperCase() + ratingLevel.label.slice(1)}>
                              <Icon className={`h-5 w-5 ${RATING_TEXT_COLORS[ratingLevel.value - 1]}`} />
                            </div>
                          );
                        })()
                      }

                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500">No interviews scheduled yet.</p>
                )}
              </CardContent>
            </Card>

            {/* People Card */}
            <Card className="bg-white shadow-none sm:shadow-lg rounded-none sm:rounded-lg mx-0">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>People</CardTitle>
                <Button size="sm" variant="outline" onClick={() => openModal('person')}><PlusCircle className="mr-2 h-4 w-4" /> Add</Button>
              </CardHeader>
              <CardContent>
                {jobData.persons && jobData.persons.length > 0 ? (
                  jobData.persons.map(person => (
                    <div
                      key={person.id}
                      className="flex items-center space-x-3 p-2 border-b last:border-b-0 cursor-pointer hover:bg-gray-50 rounded group relative"
                      onClick={() => openModal('person', person)}
                    >
                      <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-gray-500 font-bold">
                        {person.name.substring(0, 1).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 break-words">{person.name}</p>
                        {person.role && <p className="text-xs text-gray-500 break-words">{person.role}</p>}
                        {/* Show tags as badges if present */}
                        {person.notes && person.notes.some(n => n.note_type === 'tags') && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {person.notes.filter(n => n.note_type === 'tags').flatMap(n => n.note.split(',')).map((tag, idx) => (
                              <Badge key={idx} variant="secondary">{tag.trim()}</Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={e => { e.stopPropagation(); openModal('person', person); }}
                        aria-label="Edit person"
                      >
                        <Edit3 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500">No contacts added yet.</p>
                )}
              </CardContent>
            </Card>

            {/* Person Modal */}
            <Dialog open={isModalOpen && modalContent === 'person'} onOpenChange={setIsModalOpen}>
              <DialogContent className="w-[100vw] sm:w-full sm:max-w-lg mx-0 sm:mx-auto">
                <DialogHeader>
                  <DialogTitle>{editingPersonId ? 'Edit Person' : 'Add Person'}</DialogTitle>
                  <DialogDescription>
                    {editingPersonId ? 'Edit this person’s details and notes.' : 'Add a new person you interacted with.'}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Name</Label>
                    <Input value={currentPersonName} onChange={e => setCurrentPersonName(e.target.value)} placeholder="Full name" />
                  </div>
                  <div>
                    <Label>Role</Label>
                    <Input value={currentPersonRole} onChange={e => setCurrentPersonRole(e.target.value)} placeholder="e.g. Hiring Manager, Recruiter" />
                  </div>

                  {/* Notes Section */}
                  <div>
                    <Label>Notes</Label>
                    <div className="space-y-2">
                      {/* Existing notes */}
                      {currentPersonNotes.length > 0 && (
                        <div className="space-y-2">
                          {currentPersonNotes.map((note, idx) => {
                            let Icon = FileText;
                            if (note.type === 'linkedin') Icon = Linkedin;
                            else if (note.type === 'email') Icon = Mail;
                            else if (note.type === 'phone') Icon = Phone;
                            else if (note.type === 'website') Icon = Globe;
                            else if (note.type === 'date') Icon = CalendarIcon;
                            else if (note.type === 'sentiment') Icon = ThumbsUp;
                            else if (note.type === 'social') Icon = Share2;
                            else if (note.type === 'tags') Icon = Tag;
                            return (
                              <div key={idx} className="flex items-center gap-2 bg-gray-100 rounded px-2 py-1">
                                <Icon className="h-4 w-4 text-gray-500" />
                                <span className="text-xs font-medium text-gray-700 break-all">
                                  {note.type === 'date' && note.value ? format(new Date(note.value), 'PPP') :
                                    note.type === 'sentiment' ? (() => {
                                      const ratingIdx = Number(note.value) - 1;
                                      const SentimentIcon = RATING_LEVELS[ratingIdx]?.IconComponent;
                                      return (
                                        <span className="flex items-center gap-1">
                                          {RATING_LEVELS[ratingIdx]?.label || note.value}
                                          {SentimentIcon && (
                                            <SentimentIcon className={`h-4 w-4 ${RATING_TEXT_COLORS[ratingIdx]}`} />
                                          )}
                                        </span>
                                      );
                                    })() : note.type === 'tags' ? (
                                      note.value.split(',').map((tag, i) => <Badge key={i} variant="secondary">{tag.trim()}</Badge>)
                                    ) : note.value
                                  }
                                </span>
                                <Button variant="ghost" size="icon" className="ml-auto" onClick={() => handleRemovePersonNote(idx)} aria-label="Remove note"><Trash2 className="h-4 w-4 text-red-500" /></Button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {/* Add new note */}
                      <div className="flex flex-col gap-2 border rounded p-2 bg-gray-50">
                        <div className="flex gap-2 items-center">
                          <Select value={newNoteType} onValueChange={setNewNoteType}>
                            <SelectTrigger className="w-40">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {NOTE_TYPES.map(nt => (
                                <SelectItem key={nt.value} value={nt.value}>{nt.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {/* Render input based on note type */}
                          {newNoteType === 'note' && (
                            <Input value={newNoteValue} onChange={e => setNewNoteValue(e.target.value)} placeholder="Add note..." className="flex-1" />
                          )}
                          {newNoteType === 'linkedin' && (
                            <Input value={newNoteValue} onChange={e => setNewNoteValue(e.target.value)} placeholder="LinkedIn profile URL" className="flex-1" />
                          )}
                          {newNoteType === 'email' && (
                            <Input value={newNoteValue} onChange={e => setNewNoteValue(e.target.value)} placeholder="Email address" className="flex-1" />
                          )}
                          {newNoteType === 'phone' && (
                            <Input value={newNoteValue} onChange={e => setNewNoteValue(e.target.value)} placeholder="Phone number" className="flex-1" />
                          )}
                          {newNoteType === 'website' && (
                            <Input value={newNoteValue} onChange={e => setNewNoteValue(e.target.value)} placeholder="Website or portfolio" className="flex-1" />
                          )}
                          {newNoteType === 'social' && (
                            <Input value={newNoteValue} onChange={e => setNewNoteValue(e.target.value)} placeholder="Social media profile/link" className="flex-1" />
                          )}
                          {newNoteType === 'date' && (
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button variant="outline" className="w-36 justify-start text-left">
                                  {selectedDate ? format(selectedDate, 'PPP') : 'Pick a date'}
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0">
                                <Calendar
                                  mode="single"
                                  selected={selectedDate}
                                  onSelect={date => {
                                    setSelectedDate(date);
                                    setNewNoteValue(date ? date.toISOString() : '');
                                  }}
                                  initialFocus
                                />
                              </PopoverContent>
                            </Popover>
                          )}
                          {newNoteType === 'sentiment' && (
                            <Select value={String(selectedSentiment)} onValueChange={val => { setSelectedSentiment(Number(val)); setNewNoteValue(val); }}>
                              <SelectTrigger className="w-36">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {RATING_LEVELS.map(rl => (
                                  <SelectItem key={rl.value} value={String(rl.value)}>
                                    <span className="flex items-center gap-2"><rl.IconComponent className={`h-4 w-4 ${RATING_TEXT_COLORS[rl.value - 1]}`} /> {rl.label}</span>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                          {newNoteType === 'tags' && (
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button variant="outline" className="w-40 justify-start text-left">
                                  {selectedTags.length > 0 ? selectedTags.join(', ') : 'Pick tags'}
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-56">
                                <div className="flex flex-col gap-1 max-h-48 overflow-y-auto">
                                  {PERSON_TAGS.map(tag => (
                                    <label key={tag} className="flex items-center gap-2 cursor-pointer">
                                      <Checkbox checked={selectedTags.includes(tag)} onCheckedChange={checked => {
                                        setSelectedTags(checked ? [...selectedTags, tag] : selectedTags.filter(t => t !== tag));
                                        setNewNoteValue(
                                          checked ? [...selectedTags, tag].join(',') : selectedTags.filter(t => t !== tag).join(',')
                                        );
                                      }} />
                                      <span>{tag}</span>
                                    </label>
                                  ))}
                                </div>
                              </PopoverContent>
                            </Popover>
                          )}
                          <Button variant="outline" size="icon" onClick={handleAddPersonNote} aria-label="Add note"><PlusCircle className="h-5 w-5" /></Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <DialogFooter className="flex flex-col sm:flex-row items-center justify-between gap-2 pt-6">
                  <div className="flex gap-2 w-full">
                    {editingPersonId && (
                      <Button variant="destructive" onClick={handleDeletePerson} className="flex-1"><Trash2 className="mr-2 h-4 w-4" /> Delete</Button>
                    )}
                    <Button onClick={handleModalSubmit} loading={isSubmittingModal} className="flex-1">
                      {editingPersonId ? 'Save Changes' : 'Add Person'}
                    </Button>
                  </div>
                </DialogFooter>
              </DialogContent>
            </Dialog>

          </div>
        </div>



      </div>
      {/* Premium modal for resume analysis */}
      <PremiumLockModal
        open={showPremiumModal}
        onOpenChange={setShowPremiumModal}
        featureLabel="Resume Analysis"
        description="Resume analysis is a Premium feature. Upgrade to generate insights tailored to this job."
        ctaLabel="Get Premium"
      />
    </DashboardLayout>
  );
}
